import { AbstractControl, AsyncValidatorFn, UntypedFormArray, UntypedFormGroup, ValidatorFn } from '@angular/forms';

/**
 * NOTE: As per angular 13, this feature will be OOB within angular forms API therefore will be subject of deprecation
 * Takes an AbstractControl, reads current validator fns and concatenates nextValidators
 * @param currentValidators
 * @param nextValidators
 */
function extender<T>(currentValidators: T | T[], nextValidators: T[]): T[] {
  if (currentValidators != null) {
    return Array.isArray(currentValidators)
      ? [...currentValidators, ...nextValidators]
      : [currentValidators, ...nextValidators];
  }
  return nextValidators;
}

/**
 * ValidatorFn extender for sync validations
 * @param ctrl
 * @param validatorFn
 */
export function extendControlValidators(ctrl: AbstractControl, validatorFn: ValidatorFn[]): void {
  // using getter 'validator' is returning a composed function that
  // can cause base validators like required to not being detected as part control validators
  // we need to use private  _rawValidators and setValidators so we keep consistent of references
  // so validators can be found while replacing them
  const currentValidators = ctrl['_rawValidators'];
  const extendedValidators = extender(currentValidators, validatorFn);
  ctrl.setValidators(extendedValidators);
}

/**
 * ValidatorFn extender for Async validations
 * @param ctrl
 * @param validatorFn
 */
export function extendControlAsyncValidators(ctrl: AbstractControl, validatorFn: AsyncValidatorFn[]): void {
  const currentValidators = ctrl.asyncValidator;
  const extendedValidators = extender(currentValidators, validatorFn);
  ctrl.setAsyncValidators(extendedValidators);
}

/**
 * Adds an error to existing control error set
 * @param ctrl
 * @param errorName
 */
export function addControlError(ctrl: AbstractControl, errorName: string) {
  const errorEntry = { [errorName]: true };
  if (ctrl.errors == null) {
    ctrl.setErrors(errorEntry);
  } else if (!ctrl.hasError(errorName)) {
    ctrl.setErrors(Object.assign({}, ctrl.errors, errorEntry));
  }
}

/**
 * Removes an error from existing control error set
 * @param ctrl
 * @param errorName
 */
export function removeControlError(ctrl: AbstractControl, errorName: string | string[]) {
  let errorNames = Array.isArray(errorName) ? errorName : [errorName];
  if (ctrl.errors != null) {
    const nextErrorsEntries = Object.keys(ctrl.errors).filter(key => !errorNames.includes(key));
    if (nextErrorsEntries.length === 0) {
      ctrl.setErrors(null);
    } else {
      const nextErrorsMap = Object.assign({}, ...nextErrorsEntries.map(err => ({ [err]: true })));
      ctrl.setErrors(nextErrorsMap);
    }
  }
}

/**
 * Removes all validations from a control, if control is FormArray or FormGroup remove recursively for every ControlForm
 * @param control
 */
export function removeRecursiveValidators(control: AbstractControl): void {
  if (control.invalid) {
    if (control instanceof UntypedFormArray || control instanceof UntypedFormGroup) {
      Object.values(control.controls).forEach(innerControl => removeRecursiveValidators(innerControl));
    } else {
      control.setValidators(null);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }
}

export function controlContainsError(ctrl: AbstractControl | AbstractControl[], errorName: string): boolean {
  if (Array.isArray(ctrl)) {
    return ctrl.some(c => controlContainsError(c, errorName));
  }
  return ctrl?.errors != null && ctrl.hasError(errorName);
}
